import { FC, ReactNode } from 'react';

import { AxiosCartError, CartError } from '@telialt/models/src/EshopModel';
import { RequestStatus } from '@telialt/models/src/RequestModel';
import { selectIsCartAnonymous } from '@telialt/utils/src/eshopSelectors';
import { updateCartSession, updateStartOverLink } from '@telialt/utils/src/eshopUtils';
import { getIsUserAuthorized } from '@telialt/utils/src/SSO/utils';

import { useCloneCartMutation } from '../hooks/mutations/useCloneCartMutation';
import { useForceCreateCart } from '../hooks/mutations/useForceCreateCart';
import { useMigrateCartMutation } from '../hooks/mutations/useMigrateCartMutation';
import { useStrategyCreateCartQuery } from '../hooks/queries/useStrategyCreateCartQuery';

type Props = {
    baseProductCode: string;
    loadingPlaceholder: ReactNode;
    ErrorComponent?: FC<{ error: AxiosCartError | CartError | Error }>;
    children?: ReactNode;
};
/**
 * ## not to import directly, only by <CartInitializer.StrategyCreate
 *
 *  @see CartInitializer.StrategyCreate
 *  */
export const StrategyCreate: FC<Props> = ({ baseProductCode, children, loadingPlaceholder, ErrorComponent }) => {
    const {
        forceCreateCart,
        isLoadingForceCreateCart,
        isErrorForceCreateCart,
        errorForceCreateCart,
        statusForceCreateCart,
    } = useForceCreateCart();

    const { migrateCart, isErrorMigrateCart, isLoadingMigrateCart, statusMigrateCart } = useMigrateCartMutation({
        onError: forceCreateCart,
    });

    const { isErrorCloneCart, isLoadingCloneCart, cloneCart, statusCloneCart } = useCloneCartMutation({
        onError: forceCreateCart,
        onSuccess: (data) => {
            if (selectIsCartAnonymous(data) && getIsUserAuthorized()) {
                migrateCart({ oldCartId: data.guid });
            }
            history.replaceState({}, document.title, window.location.pathname);
        },
    });

    const {
        data: cart,
        isError: isErrorLoadCart,
        status: statusLoadCart,
    } = useStrategyCreateCartQuery(baseProductCode, {
        onError: forceCreateCart,
    });

    if (cart) {
        updateCartSession(cart);
        updateStartOverLink(cart);
    }
    if (cart?.generated && !isLoadingCloneCart) {
        cloneCart({ cartId: cart.guid });
    }

    const isError = isErrorForceCreateCart; // the last error chain is forceCreateCart;
    if (isError) {
        return ErrorComponent ? <ErrorComponent error={errorForceCreateCart} /> : <>{loadingPlaceholder}</>;
    }

    const isLoading =
        statusLoadCart === RequestStatus.Pending ||
        (cart?.generated && statusCloneCart === RequestStatus.Idle) ||
        isLoadingCloneCart ||
        (selectIsCartAnonymous(cart) && getIsUserAuthorized() && statusMigrateCart === RequestStatus.Idle) ||
        isLoadingMigrateCart ||
        ((isErrorLoadCart || isErrorMigrateCart || isErrorCloneCart) && statusForceCreateCart === RequestStatus.Idle) ||
        isLoadingForceCreateCart;

    if (isLoading) {
        return <>{loadingPlaceholder}</>;
    }

    return <>{children}</>;
};
